import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import toast from "react-hot-toast";

import Button from "../../components/ui/button/Button";
import { LoadingSpinner } from "../../components/ui";
import { getErrorMessage } from "../../utils/axios.utils";
import { ExamRegistrationService } from "../../services/exam-reg.service";
import { PaymentService } from "../../services/payment.service";
import { ExamRegistration } from "../../types/types";

const SchoolExamPayment = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [examRegistration, setExamRegistration] =
    useState<ExamRegistration | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    const fetchExamRegistration = async () => {
      try {
        setLoading(true);
        if (id) {
          const { data } = await ExamRegistrationService.getRegistration(id);
          setExamRegistration(data);
        }
      } catch (error) {
        console.log(getErrorMessage(error));
        toast.error("Failed to fetch exam registration");
      } finally {
        setLoading(false);
      }
    };

    fetchExamRegistration();
  }, [id]);

  const handleMakePayment = () => {
    setShowConfirmModal(true);
  };

  const confirmPayment = async () => {
    try {
      setSubmitting(true);

      if (!id) {
        toast.error("Invalid exam registration ID");
        return;
      }

      // Create payment payload
      const paymentPayload = {
        exam_registration_ids: [parseInt(id)],
        payment_method: "bank_transfer", // This could be configurable
        transaction_id: `TXN-${Date.now()}`, // This should be generated properly
      };

      // Submit payment to backend
      await PaymentService.createPayment(paymentPayload);

      toast.success("Payment submitted successfully! Status is now pending.");
      setShowConfirmModal(false);

      // Refresh the data to show updated payment status
      if (id) {
        const { data } = await ExamRegistrationService.getRegistration(id);
        setExamRegistration(data);
      }
    } catch (error) {
      console.log(getErrorMessage(error));
      toast.error("Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!examRegistration) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-4 py-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Exam Payment Details
          </h1>
          <p className="text-gray-500 dark:text-gray-300">
            Exam registration not found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-4 py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Exam Payment Details
        </h1>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Registration Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Exam Type:
                </span>
                <span className="font-medium">
                  {examRegistration.exam_type.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Exam Session:
                </span>
                <span className="font-medium">
                  {examRegistration.exam_session.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  School:
                </span>
                <span className="font-medium">
                  {examRegistration.school.name}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Payment Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Rate/Candidate:
                </span>
                <span className="font-medium">
                  {examRegistration.exam_type.registration_fee}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Registered Students:
                </span>
                <span className="font-medium">
                  {examRegistration.total_students}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Amount Due:
                </span>
                <span className="font-medium">
                  {examRegistration.total_Fee}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-300">
                  Discount:
                </span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                <span className="text-gray-900 dark:text-white font-semibold">
                  Total Payable:
                </span>
                <span className="text-gray-900 dark:text-white font-semibold">
                  {examRegistration.total_Fee}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-300 mb-1">
              Payment Status:
            </p>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${examRegistration.payment_status === "completed"
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : examRegistration.payment_status === "pending"
                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                }`}
            >
              {examRegistration.payment_status}
            </span>
          </div>

          {examRegistration.payment_status !== "completed" && (
            <Button
              variant="success"
              size="md"
              onClick={handleMakePayment}
              disabled={submitting}
            >
              {submitting ? "Processing..." : "Make Payment"}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Payment</h3>
            <p className="mb-4">
              You are about to submit a payment request for the amount of{" "}
              <strong>{examRegistration.total_Fee}</strong>. Please follow the
              instructions sent to your email to complete the payment process.
            </p>
            <p className="mb-4">
              After payment, the status will be marked as pending and will be
              reviewed by the admin.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={confirmPayment}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Confirm Payment"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolExamPayment;
